/**
 * BlogTeaser — fetches the latest 3 blog posts from /api/blog/posts and
 * renders them in a 3-column responsive grid above the footer. Self-
 * hides when the API returns nothing.
 *
 * Extracted from Landing.jsx in the components-split refactor.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";

export default function BlogTeaser() {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    // Always route through REACT_APP_BACKEND_URL so this works both in
    // preview and on the deployed centralrentals.ca origin.
    const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    fetch(`${API}/api/blog/posts`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => {
        const raw = Array.isArray(d) ? d : [];
        // Dedupe by slug AND by normalized title so daily-salted duplicates
        // don't render side-by-side on the homepage.
        const seenSlug = new Set();
        const seenTitle = new Set();
        const unique = [];
        for (const p of raw) {
          const slug = (p.slug || "").toLowerCase();
          const titleKey = (p.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 60);
          if (seenSlug.has(slug) || seenTitle.has(titleKey)) continue;
          seenSlug.add(slug); seenTitle.add(titleKey);
          // API returns `read_minutes`; the render below expects
          // `read_mins`. Normalise once here so the UI stays untouched.
          unique.push({ ...p, read_mins: p.read_mins ?? p.read_minutes ?? 5 });
          if (unique.length >= 3) break;
        }
        setPosts(unique);
      })
      .catch(() => {});
  }, []);
  if (posts.length === 0) return null;
  return (
    <section id="blog" className="py-16 md:py-24 bg-slate-50 border-y border-slate-200">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <div className="text-xs tracking-[0.22em] uppercase font-bold text-emerald-600">From the blog</div>
            <h2 className="font-display mt-3 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-[1.05]">
              Canadian landlord know-how, fresh every week.
            </h2>
          </div>
          <Link to="/blog" data-testid="blog-teaser-view-all"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-white border border-slate-200 hover:bg-slate-100 text-slate-800 font-semibold text-sm shadow-sm">
            <BookOpen className="w-4 h-4"/> Visit the blog <ArrowRight className="w-4 h-4"/>
          </Link>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {posts.map((p, i) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-teaser-card-${i}`}
              className="group flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm hover:border-emerald-200 hover:shadow-md transition-all overflow-hidden">
              {p.hero_image && (
                <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                  <img src={p.hero_image} alt={p.hero_alt || p.title} loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                </div>
              )}
              <div className="flex flex-col flex-1 p-6">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em]">
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">{p.category}</span>
                  <span className="text-slate-400 normal-case tracking-normal font-semibold">{p.read_mins} min read</span>
                </div>
                <div className="font-display mt-3 text-lg font-bold text-slate-900 leading-snug group-hover:text-emerald-800 transition-colors">{p.title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">{p.excerpt}</p>
                <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
                  <span>{new Date(p.published_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })}</span>
                  <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">Read <ArrowRight className="w-3.5 h-3.5"/></span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
